import ApiService from '@/core/services/api.service';
import JwtService from '@/core/services/jwt.service';

async function consultar(rota, pedido) {
  if (!JwtService.getToken()) throw new Error('Sessão expirada. Entre novamente no sistema.');
  ApiService.setHeader();
  const { data } = await ApiService.postOnce(`orcamentoPaciente/access/${rota}`, pedido, { timeout: 60000 });
  if (data?.resultado !== true || data?.PERMITIDO !== true) {
    const erro = new Error(data?.erro || (data?.PERMITIDO === false ? 'Usuário sem permissão para consultar orçamentos.' : 'Não foi possível consultar os orçamentos.'));
    // Sem confirmação explícita, conserva o pedido: o servidor pode ter gravado.
    erro.gravacaoPendente = typeof data?.GRAVACAO_PENDENTE === 'boolean' ? data.GRAVACAO_PENDENTE : undefined;
    throw erro;
  }
  return data;
}

const actions = {
  async excluirOrcamentoPaciente(context, pedido) {
    const data = await consultar('excluir', pedido);
    if (data.EXCLUIDO !== true || String(data.ID_PACIENTE_ORCAMENTO_PAI) !== String(pedido.idOrcamento))
      throw new Error('Não foi possível confirmar a exclusão do orçamento. Atualize a busca antes de tentar novamente.');
    return data;
  },
  async alterarPlanoOrcamentoPaciente(context, pedido) {
    const data = await consultar('alterarPlano', pedido);
    if (data.CONFIRMACAO_NECESSARIA === true && Array.isArray(data.PROCEDIMENTOS_SEM_PRECO)) return data;
    if (!data.FORMULARIO || !Array.isArray(data.FORMULARIO.ITENS) || !data.TOTAIS)
      throw new Error('Não foi possível alterar o plano de saúde.');
    return data;
  },
  async getGraficosOrcamentosPaciente(context, pedido) {
    const data = await consultar('graficos', pedido);
    const conversao = data.CONVERSAO;
    const conversaoValida = c => c && Number.isInteger(c.FEITOS) && Number.isInteger(c.AGENDADOS) && c.FEITOS >= 0 && c.AGENDADOS >= 0 && c.AGENDADOS <= c.FEITOS &&
      Number.isFinite(c.PERCENTUAL) && Math.abs(c.PERCENTUAL - (c.FEITOS ? 100 * c.AGENDADOS / c.FEITOS : 0)) <= 0.0051;
    if (!Number.isFinite(data.TOTAL) || !Number.isFinite(data.VALOR_PAGO) || !['CATEGORIAS', 'PROCEDIMENTOS', 'USUARIOS'].every(campo =>
      Array.isArray(data[campo]) && data[campo].every(item => typeof item.ID === 'string' && typeof item.DESCRICAO === 'string' && Number.isFinite(item.TOTAL) && Number.isFinite(item.VALOR_PAGO) && Number.isFinite(item.VALOR_AGENDADO))) ||
      !conversaoValida(conversao) || !Array.isArray(conversao.USUARIOS) || !conversao.USUARIOS.every(u => typeof u.ID === 'string' && typeof u.DESCRICAO === 'string' && conversaoValida(u)) ||
      new Set(conversao.USUARIOS.map(u => u.ID)).size !== conversao.USUARIOS.length ||
      conversao.USUARIOS.reduce((s, u) => s + u.FEITOS, 0) !== conversao.FEITOS || conversao.USUARIOS.reduce((s, u) => s + u.AGENDADOS, 0) !== conversao.AGENDADOS)
      throw new Error('Não foi possível carregar os gráficos de orçamentos.');
    return data;
  },
  async getListagemOrcamentosPaciente(context, pedido) {
    const data = await consultar('listagem', pedido);
    if (!Array.isArray(data.REGISTROS) || !Number.isInteger(data.QUANTIDADE) || data.QUANTIDADE < 0 ||
        !data.REGISTROS.every(registro => Array.isArray(registro.PROCEDIMENTOS)))
      throw new Error('A consulta de orçamentos retornou dados incompletos.');
    return data;
  },
  async editarOcorrenciaOrcamento(context, pedido) {
    const data = await consultar('editarOcorrencia', pedido);
    if (!data.CHAVE || !data.FORMULARIO || !Array.isArray(data.TIPOS))
      throw new Error('Não foi possível abrir a ocorrência.');
    return data;
  },
  async salvarOcorrenciaOrcamento(context, pedido) {
    const data = await consultar('salvarOcorrencia', pedido);
    if (!data.ID_PACIENTE_OCORRENCIA) throw new Error('A gravação da ocorrência não foi confirmada.');
    return data;
  },
  async getOpcoesOrcamentoPaciente() { return consultar('opcoes', {}); },
  async getPacienteOrcamentoEdicao(context, pedido) { return consultar('paciente', pedido); },
  async getProdutosOrcamentoEdicao(context, pedido) { return consultar('produtos', pedido); },
  async editarOrcamentoPaciente(context, pedido) {
    const data = await consultar('editar', pedido);
    if (!data.CHAVE || !data.FORMULARIO || !Array.isArray(data.FORMULARIO.ITENS) || !data.CONFIGURACOES)
      throw new Error('Não foi possível abrir a edição do orçamento.');
    return data;
  },
  async calcularOrcamentoPaciente(context, pedido) {
    const data = await consultar('calcular', pedido);
    if (!data.FORMULARIO || !Array.isArray(data.FORMULARIO.ITENS) || !data.TOTAIS)
      throw new Error('Não foi possível calcular o orçamento.');
    return data;
  },
  async salvarOrcamentoPaciente(context, pedido) {
    const data = await consultar('salvar', pedido);
    if (!data.ID_PACIENTE_ORCAMENTO_PAI) throw new Error('A gravação do orçamento não foi confirmada.');
    return data;
  },
  async getRelatorioOrcamentoPaciente(context, pedido) {
    const data = await consultar('relatorio', pedido);
    if (!data.CABECALHO || !data.CONFIGURACOES || !data.TOTAIS || !data.PARCELAMENTO ||
        !Array.isArray(data.ITENS) || !data.ITENS.length ||
        !data.ITENS.every(i => Array.isArray(i.KIT) && Array.isArray(i.TAXAS))) {
      throw new Error('Não foi possível carregar o relatório do orçamento.');
    }
    return data;
  },
  async getOrcamentosPaciente(context, pedido) {
    const data = await consultar('listar', pedido);
    if (!Array.isArray(data.REGISTROS) || !Number.isInteger(data.QUANTIDADE) || data.QUANTIDADE < 0) {
      throw new Error('A consulta de orçamentos retornou dados incompletos.');
    }
    return data;
  },
  async getOrcamentoPaciente(context, pedido) {
    const data = await consultar('obter', pedido);
    if (!['CABECALHOS', 'ITENS', 'AGENDAMENTOS', 'OCORRENCIAS'].every(campo => Array.isArray(data[campo])) ||
        !data.CABECALHOS.length || !data.ITENS.length) throw new Error('Não foi possível carregar o detalhamento do orçamento.');
    return data;
  }
};

export default { state: {}, getters: {}, mutations: {}, actions };
